import { parseISO } from "date-fns";
import type { TFunction } from "i18next";
import { useWatch } from "react-hook-form";

import type { CampaignJobBoardFormControl } from "./types";

import { Checkbox } from "@/components/ui/v2/checkbox";
import {
  FormDescription,
  FormField,
  FormItem,
} from "@/components/ui/v2/form";
import { FormattedCurrencyInput } from "@/components/ui/v2/formatted-currency-input";
import { getProRataFirstMonthBudget } from "@/utils/dateUtils";

interface ProRataFirstMonthFieldProps {
  control: CampaignJobBoardFormControl;
  useProRataFirstMonth: boolean;
  showCurrentMonthBudget: boolean;
  daysRemainingInMonth: number | null;
  t: TFunction;
}

/**
 * Indeed-only, create-only "pro rata first month" control. Shown under the same gate as the manual
 * current-month budget (start date in the current month). The estimated prorated amount is computed
 * live from monthly budget + start date and shown read-only — the backend allocator persists the
 * authoritative value, and Indeed prorates the first month server-side.
 */
export function ProRataFirstMonthField({
  control,
  useProRataFirstMonth,
  showCurrentMonthBudget,
  daysRemainingInMonth,
  t,
}: ProRataFirstMonthFieldProps) {
  // Watch before any early return (rules of hooks).
  const watchedMonthlyBudget = useWatch({ control, name: "monthlyBudget" });
  const watchedStartDate = useWatch({ control, name: "startDate" });

  if (!showCurrentMonthBudget || daysRemainingInMonth === null) {
    return null;
  }

  const proratedAmount =
    watchedMonthlyBudget && watchedStartDate
      ? getProRataFirstMonthBudget(watchedMonthlyBudget, parseISO(watchedStartDate))
      : undefined;

  return (
    <div className="mt-6 pt-6 border-t border-border">
      <FormField
        control={control}
        name="useProRataFirstMonth"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-start space-x-3 mb-1">
              <Checkbox
                id="useProRataFirstMonth"
                checked={field.value ?? false}
                onCheckedChange={(checked) => {
                  field.onChange(checked === true);
                }}
                className="mt-0.5"
              />
              <label
                htmlFor="useProRataFirstMonth"
                className="text-sm text-foreground cursor-pointer leading-relaxed"
              >
                {t("admin.campaigns.jobBoard.proRataFirstMonth.label", {
                  defaultValue:
                    "Charge pro rata for the first month ({{count}} days remaining)",
                  count: daysRemainingInMonth,
                })}
              </label>
            </div>
            <FormDescription className="ml-7">
              {t("admin.campaigns.jobBoard.proRataFirstMonth.description", {
                defaultValue:
                  "Indeed receives the full monthly budget and prorates the first month itself, so the campaign can run a partial month below the normal minimum.",
              })}
            </FormDescription>
          </FormItem>
        )}
      />

      {useProRataFirstMonth && (
        <div className="mt-3 space-y-2">
          <span className="text-sm font-medium text-foreground">
            {t("admin.campaigns.jobBoard.proRataFirstMonth.amountLabel", {
              defaultValue: "Estimated first month budget",
            })}
          </span>
          <FormattedCurrencyInput value={proratedAmount} decimals disabled />
          <p className="text-sm text-muted-foreground">
            {t("admin.campaigns.jobBoard.proRataFirstMonth.amountDescription", {
              defaultValue:
                "Estimated charge for the remaining {{count}} days of the current month. Computed automatically and cannot be edited.",
              count: daysRemainingInMonth,
            })}
          </p>
        </div>
      )}
    </div>
  );
}
